import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { siteConfig } from '../data/mock';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-lavender-600 hover:text-lavender-700 transition-colors"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 
            className="text-3xl md:text-4xl font-semibold text-grape-800 mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Privacy Policy
          </h1>
          
          <div 
            className="prose prose-lg max-w-none text-grape-700"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            <p className="text-sm text-lavender-600 mb-8">
              Last updated: July 1, 2025
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Introduction
              </h2>
              <p className="mb-4">
                Welcome to {siteConfig.businessName}. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our event planning services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Information We Collect
              </h2>
              <p className="mb-4">We may collect information that you provide directly to us, including:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Name and contact information (email address, phone number)</li>
                <li>Event details (event type, date, location preferences)</li>
                <li>Messages and communications you send to us</li>
                <li>Any other information you choose to provide</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                How We Use Your Information
              </h2>
              <p className="mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Respond to your inquiries and consultation requests</li>
                <li>Provide our event planning and coordination services</li>
                <li>Communicate with you about your events</li>
                <li>Send you updates about our services (with your consent)</li>
                <li>Improve our website and services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Information Sharing
              </h2>
              <p className="mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>With vendors and service providers who assist in delivering our event planning services (with your consent)</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and safety</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Data Security
              </h2>
              <p className="mb-4">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Your Rights
              </h2>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt-out of marketing communications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Cookies
              </h2>
              <p className="mb-4">
                Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse cookies, but some features of our website may not function properly without them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Changes to This Policy
              </h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-grape-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Contact Us
              </h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy or our practices, please contact us at:
              </p>
              <div className="bg-lavender-50 p-6 rounded-xl">
                <p className="font-semibold text-grape-800">{siteConfig.businessName}</p>
                <p>Email: <a href={`mailto:${siteConfig.email}`} className="text-lavender-600 hover:text-lavender-700">{siteConfig.email}</a></p>
                <p>{siteConfig.location}</p>
              </div>
            </section>

            <section className="mt-12 pt-8 border-t border-lavender-100">
              <p className="text-sm text-lavender-600 text-center">
                © {new Date().getFullYear()} {siteConfig.businessName}. All rights reserved.
              </p>
            </section>
          </div>
        </article>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
